import { supabase } from "@/integrations/supabase/client";
import { mediaService } from "./mediaService";

export interface VoiceMessageRecord {
  id: string;
  event_id: string;
  uploaded_by: string;
  guest_name: string | null;
  file_path: string;
  file_url: string;
  duration_seconds: number | null;
  created_at: string;
  storage_provider?: string | null;
  storage_key?: string | null;
  storage_url_mode?: "public";
  google_drive_file_id?: string | null;
}

const hydrateVoiceReadUrls = async (rows: VoiceMessageRecord[]): Promise<VoiceMessageRecord[]> => (
  Promise.all(rows.map(async (row) => ({
    ...row,
    file_url: await mediaService.resolveDisplayUrl(row),
  })))
);

export const voiceMessageService = {
  async fetchByEvent(eventId: string): Promise<VoiceMessageRecord[]> {
    const { data, error } = await supabase
      .from("voice_messages")
      .select("id, event_id, uploaded_by, guest_name, file_path, file_url, duration_seconds, created_at, storage_provider, storage_key, storage_url_mode, google_drive_file_id")
      .eq("event_id", eventId)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return hydrateVoiceReadUrls((data || []) as VoiceMessageRecord[]);
  },

  async fetchByViewer(eventId: string, opts: { isEventAdmin: boolean; deviceCode?: string }): Promise<VoiceMessageRecord[]> {
    let query = supabase
      .from("voice_messages")
      .select("id, event_id, uploaded_by, guest_name, file_path, file_url, duration_seconds, created_at, storage_provider, storage_key, storage_url_mode, google_drive_file_id")
      .eq("event_id", eventId);

    if (!opts.isEventAdmin) {
      if (!opts.deviceCode) return [];
      query = query.eq("uploaded_by", opts.deviceCode);
    }

    const { data, error } = await query.order("created_at", { ascending: false });
    if (error) throw error;
    return hydrateVoiceReadUrls((data || []) as VoiceMessageRecord[]);
  },

  async uploadMessage(params: {
    audioFile: File;
    eventId: string;
    eventSlug: string;
    deviceCode: string;
    guestName?: string;
    durationSeconds?: number;
  }): Promise<VoiceMessageRecord | null> {
    const { audioFile, eventId, eventSlug, deviceCode, guestName, durationSeconds } = params;

    // Check if uploads are locked for this event
    const { data: lockCheck } = await supabase
      .from("events").select("uploads_locked").eq("id", eventId).single();
    if (lockCheck?.uploads_locked) {
      throw new Error("Uploadanje je zaključano za ovaj event.");
    }

    const _now = new Date();
    const mediaId = [
      _now.getFullYear(),
      String(_now.getMonth() + 1).padStart(2, "0"),
      String(_now.getDate()).padStart(2, "0"),
    ].join("") + "_" + [
      String(_now.getHours()).padStart(2, "0"),
      String(_now.getMinutes()).padStart(2, "0"),
      String(_now.getSeconds()).padStart(2, "0"),
      String(_now.getMilliseconds()).padStart(3, "0"),
    ].join("");
    const { filePath, fileUrl, storageMetadata } = await mediaService.uploadEventMedia({
      eventSlug,
      deviceCode,
      mediaId,
      file: audioFile,
      kind: "voice",
      contentType: audioFile.type || "audio/webm",
      cacheControl: "3600",
    });

    const { data, error } = await supabase
      .from("voice_messages")
      .insert({
        event_id: eventId,
        uploaded_by: deviceCode,
        guest_name: guestName?.trim() || null,
        file_path: filePath,
        file_url: fileUrl,
        duration_seconds: durationSeconds ?? null,
        file_size: audioFile.size,
        ...storageMetadata,
      } as any)
      .select("id, event_id, uploaded_by, guest_name, file_path, file_url, duration_seconds, created_at, storage_provider, storage_key, storage_url_mode, google_drive_file_id")
      .single();

    if (error) return null;
    return data ? { ...(data as VoiceMessageRecord), file_url: await mediaService.resolveDisplayUrl(data as VoiceMessageRecord) } : null;
  },

  async deleteMessage(id: string): Promise<void> {
    const { data: existing, error: fetchError } = await supabase
      .from("voice_messages")
      .select("file_path")
      .eq("id", id)
      .maybeSingle();

    if (fetchError) throw fetchError;

    if (existing?.file_path) {
      await mediaService.deleteEventMedia([existing.file_path]);
    }

    const { error } = await supabase
      .from("voice_messages")
      .delete()
      .eq("id", id);

    if (error) throw error;
  },
};
